import React, { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  Title3,
  Button,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Spinner,
  MessageBar,
  MessageBarBody,
  Tooltip,
  Input,
  Label,
  Dropdown,
  Option
} from '@fluentui/react-components';
import {
  History24Regular,
  Filter24Regular,
  Checkmark24Regular,
  CheckmarkCircle20Regular,
  ArrowUp20Regular
} from '@fluentui/react-icons';
import { alertsService } from '../../services/alertsService';
import type {
  AlertHistoryDto,
  AlertHistoryFilters,
  AlertEntityType,
  AlertStatus,
  EffectiveSeverity
} from '../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  filterBar: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    paddingTop: '14px',
    paddingBottom: '14px',
    paddingLeft: '16px',
    paddingRight: '16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '8px',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  filterField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: '150px',
  },
  filterLabel: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: tokens.colorNeutralForeground3,
  },
  dateField: {
    width: '180px',
  },
  tableContainer: {
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: '8px',
    overflow: 'hidden',
  },
  tableHeaderRow: {
    backgroundColor: tokens.colorNeutralBackground2,
    '& > th': {
      backgroundColor: tokens.colorNeutralBackground2,
      color: tokens.colorNeutralForeground1,
      fontWeight: tokens.fontWeightBold,
      fontSize: tokens.fontSizeBase200,
      letterSpacing: '0.03em',
      textTransform: 'uppercase',
      ...shorthands.borderBottom('2px', 'solid', tokens.colorNeutralStroke1),
      paddingTop: '4px',
      paddingBottom: '4px',
    },
  },
  // Indicador sóbrio (sem fundo preenchido)
  sevIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
  },
  sevDot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  sevDotWarning: {
    backgroundColor: tokens.colorPaletteYellowForeground1,
    boxShadow: `0 0 0 3px ${tokens.colorPaletteYellowBackground1}`,
  },
  sevDotCritical: {
    backgroundColor: tokens.colorPaletteRedForeground1,
    boxShadow: `0 0 0 3px ${tokens.colorPaletteRedBackground1}`,
  },
  statusIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
  },
  statusDotActive: {
    backgroundColor: tokens.colorPaletteYellowForeground1,
  },
  statusDotResolved: {
    backgroundColor: tokens.colorPaletteGreenForeground1,
  },
  statusResolvedText: {
    color: tokens.colorNeutralForeground3,
  },
  opText: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    textTransform: 'lowercase',
  },
  valueCell: {
    fontFamily: tokens.fontFamilyMonospace,
    fontVariantNumeric: 'tabular-nums',
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightMedium,
    whiteSpace: 'nowrap',
  },
  arrowUp: {
    color: tokens.colorNeutralForeground3,
    marginLeft: '4px',
    verticalAlign: 'middle',
  },
  promotedBadge: {
    display: 'inline-block',
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorPaletteRedForeground1,
    marginLeft: '6px',
    letterSpacing: '0.02em',
  },
  emptyState: {
    padding: '32px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  loadMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '16px',
  },
  actions: {
    display: 'flex',
    gap: '4px',
    justifyContent: 'center',
  },
  cellCenter: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
  },
  cellShiftRight: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
  },
  cellRight: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    alignItems: 'center',
    textAlign: 'right',
  },
  valueCenter: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    fontFamily: tokens.fontFamilyMonospace,
    fontVariantNumeric: 'tabular-nums',
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightMedium,
    whiteSpace: 'nowrap',
  },
  valueRight: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    alignItems: 'center',
    textAlign: 'right',
    fontFamily: tokens.fontFamilyMonospace,
    fontVariantNumeric: 'tabular-nums',
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightMedium,
    whiteSpace: 'nowrap',
  },
  dateCell: {
    whiteSpace: 'nowrap',
  },
  dateContent: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '48px',
    whiteSpace: 'nowrap',
  },
  dateHeader: {
    display: 'block',
    paddingLeft: '48px',
  },
  truncateCell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

interface AlertsHistoryTableProps {
  onAlertResolved?: () => void;
}

const ENTITY_OPTIONS: { value: AlertEntityType | ''; label: string }[] = [
  { value: '', label: 'Todas' },
  { value: 'School', label: 'Escola' },
  { value: 'Section', label: 'Turma' },
  { value: 'Teacher', label: 'Professor' },
  { value: 'Student', label: 'Estudante' },
  { value: 'Staff', label: 'Funcionário' },
  { value: 'TeacherRoster', label: 'Atrib. Professor' },
  { value: 'StudentEnrollment', label: 'Matrícula' },
  { value: 'StaffRoster', label: 'Atrib. Staff' },
  { value: 'QueueDlq', label: 'Fila DLQ' },
  { value: 'QueueScheduled', label: 'Fila Reagendadas' },
  { value: 'QueueActive', label: 'Fila Ativa' }
];

const STATUS_OPTIONS: { value: AlertStatus | ''; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'Active', label: 'Ativo' },
  { value: 'Resolved', label: 'Resolvido' }
];

const SEVERITY_OPTIONS: { value: EffectiveSeverity | ''; label: string }[] = [
  { value: '', label: 'Todas' },
  { value: 'Warning', label: 'Warning' },
  { value: 'Critical', label: 'Critical' }
];

const numberFormatter = new Intl.NumberFormat('pt-BR');

export const AlertsHistoryTable: React.FC<AlertsHistoryTableProps> = ({ onAlertResolved }) => {
  const styles = useStyles();

  const [alerts, setAlerts] = useState<AlertHistoryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | undefined>();

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [entityType, setEntityType] = useState<AlertEntityType | ''>('');
  const [severity, setSeverity] = useState<EffectiveSeverity | ''>('');
  const [status, setStatus] = useState<AlertStatus | ''>('');

  const loadAlerts = useCallback(async (append = false) => {
    try {
      setLoading(true);
      setError(null);

      const filters: AlertHistoryFilters = {};
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;
      if (entityType) filters.entityType = entityType;
      if (status) filters.status = status;

      const response = await alertsService.getAlertHistory(
        filters,
        append ? nextCursor : undefined,
        50
      );

      // Filtragem client-side por severidade efetiva (backend ainda não filtra por isso)
      const filtered = severity
        ? response.alerts.filter(a => a.effectiveSeverity === severity)
        : response.alerts;

      if (append) {
        setAlerts(prev => [...prev, ...filtered]);
      } else {
        setAlerts(filtered);
      }

      setHasMore(response.hasMore);
      setNextCursor(response.nextCursor);
    } catch (err) {
      console.error('Error loading alerts:', err);
      setError('Erro ao carregar histórico de alertas');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, entityType, status, severity, nextCursor]);

  useEffect(() => {
    loadAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilter = () => {
    setNextCursor(undefined);
    loadAlerts();
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setEntityType('');
    setSeverity('');
    setStatus('');
    setNextCursor(undefined);
    setTimeout(() => loadAlerts(), 0);
  };

  const handleResolve = async (alert: AlertHistoryDto) => {
    try {
      await alertsService.resolveAlert(alert.partitionKey, alert.id);
      loadAlerts();
      onAlertResolved?.();
    } catch (err) {
      console.error('Error resolving alert:', err);
      setError('Erro ao resolver alerta');
    }
  };

  const handleResolveAll = async () => {
    if (!confirm('Deseja marcar todos os alertas ativos como resolvidos?')) return;

    try {
      setError(null);
      setInfo(null);
      const result = await alertsService.resolveAllAlerts();
      if (result.skippedCount > 0) {
        setInfo(`${result.count} alertas resolvidos · ${result.skippedCount} pulados (verifique os logs).`);
      } else {
        setInfo(`${result.count} alertas resolvidos.`);
      }
      setTimeout(() => setInfo(null), 6000);
      loadAlerts();
      onAlertResolved?.();
    } catch (err) {
      console.error('Error resolving all alerts:', err);
      setError('Erro ao resolver alertas');
    }
  };

  /**
   * Formata data em horário de Brasília (BRT, UTC-3).
   * Ignora o fuso do browser para garantir que todos os usuários vejam o mesmo horário.
   */
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Sao_Paulo'
    });
  };

  const renderSeverity = (alert: AlertHistoryDto) => {
    const dotClass = alert.effectiveSeverity === 'Critical' ? styles.sevDotCritical : styles.sevDotWarning;
    return (
      <span className={styles.sevIndicator}>
        <span className={`${styles.sevDot} ${dotClass}`} />
        {alert.effectiveSeverity}
        {alert.wasAutoPromoted && (
          <Tooltip content="Severidade promovida automaticamente por permanecer ativa" relationship="description">
            <span className={styles.promotedBadge}>↑ promovido</span>
          </Tooltip>
        )}
      </span>
    );
  };

  const renderStatus = (alert: AlertHistoryDto) => {
    const isActive = alert.status === 'Active';
    return (
      <span className={`${styles.statusIndicator} ${!isActive ? styles.statusResolvedText : ''}`}>
        <span className={`${styles.sevDot} ${isActive ? styles.statusDotActive : styles.statusDotResolved}`} />
        {isActive ? 'Ativo' : 'Resolvido'}
      </span>
    );
  };

  const hasActiveAlerts = alerts.some(a => a.status === 'Active');

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <History24Regular />
          <Title3>Histórico de Alertas</Title3>
        </div>
        {hasActiveAlerts && (
          <Button
            appearance="primary"
            icon={<Checkmark24Regular />}
            onClick={handleResolveAll}
          >
            Marcar todos como resolvidos
          </Button>
        )}
      </div>

      {info && (
        <MessageBar intent="success">
          <MessageBarBody>{info}</MessageBarBody>
        </MessageBar>
      )}

      {/* Filter Bar */}
      <div className={styles.filterBar}>
        <div className={`${styles.filterField} ${styles.dateField}`}>
          <Label className={styles.filterLabel}>Data Início</Label>
          <Input
            type="datetime-local"
            value={startDate}
            onChange={(_, data) => setStartDate(data.value)}
          />
        </div>
        <div className={`${styles.filterField} ${styles.dateField}`}>
          <Label className={styles.filterLabel}>Data Fim</Label>
          <Input
            type="datetime-local"
            value={endDate}
            onChange={(_, data) => setEndDate(data.value)}
          />
        </div>
        <div className={styles.filterField}>
          <Label className={styles.filterLabel}>Entidade</Label>
          <Dropdown
            value={ENTITY_OPTIONS.find(o => o.value === entityType)?.label || 'Todas'}
            onOptionSelect={(_, data) => setEntityType((data.optionValue as AlertEntityType | '') || '')}
          >
            {ENTITY_OPTIONS.map(opt => (
              <Option key={opt.value || 'all'} value={opt.value}>{opt.label}</Option>
            ))}
          </Dropdown>
        </div>
        <div className={styles.filterField}>
          <Label className={styles.filterLabel}>Severidade</Label>
          <Dropdown
            value={SEVERITY_OPTIONS.find(o => o.value === severity)?.label || 'Todas'}
            onOptionSelect={(_, data) => setSeverity((data.optionValue as EffectiveSeverity | '') || '')}
          >
            {SEVERITY_OPTIONS.map(opt => (
              <Option key={opt.value || 'all'} value={opt.value}>{opt.label}</Option>
            ))}
          </Dropdown>
        </div>
        <div className={styles.filterField}>
          <Label className={styles.filterLabel}>Status</Label>
          <Dropdown
            value={STATUS_OPTIONS.find(o => o.value === status)?.label || 'Todos'}
            onOptionSelect={(_, data) => setStatus((data.optionValue as AlertStatus | '') || '')}
          >
            {STATUS_OPTIONS.map(opt => (
              <Option key={opt.value || 'all'} value={opt.value}>{opt.label}</Option>
            ))}
          </Dropdown>
        </div>
        <Button
          appearance="secondary"
          icon={<Filter24Regular />}
          onClick={handleFilter}
        >
          Filtrar
        </Button>
        <Button
          appearance="subtle"
          onClick={handleClearFilters}
        >
          Limpar
        </Button>
      </div>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {loading && alerts.length === 0 ? (
        <Spinner size="medium" label="Carregando..." />
      ) : alerts.length === 0 ? (
        <div className={styles.emptyState}>
          Nenhum alerta encontrado com os filtros selecionados.
        </div>
      ) : (
        <>
          <div className={styles.tableContainer}>
            <Table size="small" style={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHeader>
                <TableRow className={styles.tableHeaderRow}>
                  <TableHeaderCell style={{ width: '11%' }}>Severidade</TableHeaderCell>
                  <TableHeaderCell style={{ width: '17%' }}>Entidade</TableHeaderCell>
                  <TableHeaderCell style={{ width: '8%' }}>Operação</TableHeaderCell>
                  <TableHeaderCell style={{ width: '9%' }}>
                    <div className={styles.cellRight}>Limite</div>
                  </TableHeaderCell>
                  <TableHeaderCell style={{ width: '11%' }}>
                    <div className={styles.cellRight}>Observado</div>
                  </TableHeaderCell>
                  <TableHeaderCell style={{ width: '15%' }}>
                    <span className={styles.dateHeader}>Disparado em</span>
                  </TableHeaderCell>
                  <TableHeaderCell style={{ width: '7%' }}>
                    <div className={styles.cellCenter}>Notif.</div>
                  </TableHeaderCell>
                  <TableHeaderCell style={{ width: '12%', paddingLeft: '40px' }}>
                    <div className={styles.cellShiftRight}>Status</div>
                  </TableHeaderCell>
                  <TableHeaderCell style={{ width: '10%' }}>
                    <div className={styles.cellCenter}>Ação</div>
                  </TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alerts.map(alert => {
                  const exceeded = alert.actualValue > alert.thresholdValue;
                  return (
                    <TableRow key={`${alert.partitionKey}-${alert.id}`}>
                      <TableCell>{renderSeverity(alert)}</TableCell>
                      <TableCell className={styles.truncateCell}>{alert.entityLabel}</TableCell>
                      <TableCell><span className={styles.opText}>{alert.operationType.toLowerCase()}</span></TableCell>
                      <TableCell>
                        <div className={styles.valueRight}>
                          {numberFormatter.format(alert.thresholdValue)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={styles.valueRight}>
                          {numberFormatter.format(alert.actualValue)}
                          {exceeded && (
                            <ArrowUp20Regular className={styles.arrowUp} fontSize={12} />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={styles.dateContent}>{formatDate(alert.firstTriggeredAt)}</div>
                      </TableCell>
                      <TableCell>
                        <div className={styles.valueCenter}>
                          {alert.notificationCount}x
                        </div>
                      </TableCell>
                      <TableCell style={{ paddingLeft: '40px' }}>
                        <div className={styles.cellShiftRight}>
                          {renderStatus(alert)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={styles.actions}>
                          {alert.status === 'Active' && (
                            <Tooltip content="Marcar como Resolvido" relationship="label">
                              <Button
                                appearance="subtle"
                                size="small"
                                icon={<CheckmarkCircle20Regular />}
                                onClick={() => handleResolve(alert)}
                              />
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {hasMore && (
            <div className={styles.loadMoreContainer}>
              <Button
                appearance="secondary"
                onClick={() => loadAlerts(true)}
                disabled={loading}
              >
                {loading ? <Spinner size="tiny" /> : 'Carregar mais'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
